import { useState, useEffect, useMemo } from 'react'

import { Bot, LayoutDashboard, Target } from 'lucide-react'

import { Metadata } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { PageHeaderLayout } from 'src/components/Core/PageHeaderLayout'
import { RefreshButton } from 'src/components/Core/RefreshButton'
import {
  SegmentedTabs,
  type SegmentedTabItem,
} from 'src/components/Core/SegmentedTabs'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'

import { ActivityAnalyticsSectionTabs } from './components/ActivityAnalyticsSectionTabs'
import { CallAnalyticsHeroStrip } from './components/CallAnalyticsHeroStrip'
import { VoiceAgentHeroStrip } from './components/VoiceAgentHeroStrip'
import { FiltersSlotProvider } from './components/FiltersPortal'
import {
  CallAnalyticsTab,
  CALL_ANALYTICS_TAB_LABELS,
  resolveTabFromUrl,
  type CallAnalyticsTabValue,
} from './tabs'
import CallAnalyticsOverviewTab from './tabs/CallAnalyticsTab'
import VoiceAgentTab from './tabs/VoiceAgentTab'
import CallQualityTab from './tabs/CallQuality/CallQualityTab'
import { setTabSearchParam } from 'src/lib/url-search-params.utils'

import { CALL_ANALYTICS_FILTERS_CHANGED_EVENT, getUrlParams } from './utils'

// Target icon with an orange dot in the top-right corner — used on tabs that
// are admin-only so admins can see at a glance which tabs are hidden from others.
const CallQualityAdminIcon = ({
  size,
  className,
}: {
  size?: number
  className?: string
}) => (
  <span className="relative inline-flex">
    <Target size={size} className={className} />
    <span
      aria-hidden="true"
      data-testid="admin-only-indicator"
      className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-orange-500"
    />
  </span>
)

const CallAnalyticsPage = () => {
  const company = useCurrentCompanyContext()
  const { currentUser } = useAuth()
  const isVoiceAgentEnabled = company?.voiceAgentEnabled
  const isAdmin = Boolean(currentUser?.roles?.includes('ADMIN'))
  const [selectedTab, setSelectedTab] = useState<CallAnalyticsTabValue>(
    CallAnalyticsTab.Overview
  )
  const [refreshKey, setRefreshKey] = useState(0)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)

  useEffect(() => {
    if (company && currentUser) {
      const tabFromUrl = new URLSearchParams(window.location.search).get('tab')
      const initialTab = resolveTabFromUrl(tabFromUrl)
      // Prevent non-admin users from accessing call quality tab via URL
      const initialTabWithPermissions: CallAnalyticsTabValue = isAdmin
        ? initialTab
        : initialTab === CallAnalyticsTab.CallQuality
        ? CallAnalyticsTab.Overview
        : initialTab
      const finalTab: CallAnalyticsTabValue = isVoiceAgentEnabled
        ? initialTabWithPermissions
        : CallAnalyticsTab.Overview
      // Rewrite the URL when the raw value differs from the canonical one
      // — covers both aliases (?tab=general) and gated-tab redirects.
      if (tabFromUrl !== finalTab) {
        setTabSearchParam(finalTab)
      }
      setSelectedTab(finalTab)
    }
  }, [company, currentUser, isVoiceAgentEnabled, isAdmin])

  const handleTabChange = (tab: CallAnalyticsTabValue) => {
    setSelectedTab(tab)
    setTabSearchParam(tab)
  }

  const tabs = useMemo<
    ReadonlyArray<SegmentedTabItem<CallAnalyticsTabValue>>
  >(() => {
    const items: SegmentedTabItem<CallAnalyticsTabValue>[] = [
      {
        value: CallAnalyticsTab.Overview,
        label: CALL_ANALYTICS_TAB_LABELS[CallAnalyticsTab.Overview],
        icon: LayoutDashboard,
      },
    ]
    if (isVoiceAgentEnabled) {
      items.push({
        value: CallAnalyticsTab.VoiceAgent,
        label: CALL_ANALYTICS_TAB_LABELS[CallAnalyticsTab.VoiceAgent],
        icon: Bot,
      })
    }
    if (isVoiceAgentEnabled && isAdmin) {
      items.push({
        value: CallAnalyticsTab.CallQuality,
        label: CALL_ANALYTICS_TAB_LABELS[CallAnalyticsTab.CallQuality],
        icon: CallQualityAdminIcon,
        title: 'Admin-only — hidden from non-admins',
      })
    }
    return items
  }, [isVoiceAgentEnabled, isAdmin])

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1)
    setLastUpdatedAt(new Date())
  }

  const [filtersSlot, setFiltersSlot] = useState<HTMLElement | null>(null)

  // Tabs' FiltersBars write to the URL via `updateUrl` (synchronous
  // `replaceState` — which doesn't fire popstate). Without an explicit
  // signal the page-level hero strip can't tell when those filters change
  // and would stay stale until the next Refresh click. `updateUrl`
  // dispatches a custom event after every write; we bump `filtersVersion`
  // on receipt so the heroFilters useMemo below re-reads the URL.
  const [filtersVersion, setFiltersVersion] = useState(0)
  useEffect(() => {
    const handler = () => setFiltersVersion((v) => v + 1)
    window.addEventListener(CALL_ANALYTICS_FILTERS_CHANGED_EVENT, handler)
    return () =>
      window.removeEventListener(CALL_ANALYTICS_FILTERS_CHANGED_EVENT, handler)
  }, [])

  // Hero-strip filters: URL is the source of truth. Re-reads on either
  // (a) Refresh-button click (`refreshKey`) or (b) any filter change in
  // any tab (`filtersVersion`, bumped via the custom event above).
  const heroFilters = useMemo(() => {
    const p = getUrlParams()
    return {
      dateRange: { gte: p.defaultStartDate, lte: p.defaultEndDate },
      recruiters: p.defaultRecruiters,
      jobListings: p.defaultJobListings,
      leadSources: p.defaultLeadSources,
      callDirections: p.defaultCallDirections,
      callStatuses: p.defaultCallStatuses,
      callTypes: p.defaultCallTypes,
      connectedStatuses: p.defaultConnectedStatus,
      voiceAgents: p.defaultVoiceAgents,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, filtersVersion])

  return (
    <PageHeaderLayout
      title="Call Analytics"
      tabs={
        <div className="flex items-center gap-2">
          <ActivityAnalyticsSectionTabs section="calls" />
          <SegmentedTabs<CallAnalyticsTabValue>
            tabs={tabs}
            value={selectedTab}
            onChange={handleTabChange}
            ariaLabel="Call analytics sections"
          />
        </div>
      }
      toolbar={
        <>
          <div
            ref={setFiltersSlot}
            data-testid="call-analytics-filters-slot"
            className="flex flex-wrap items-center gap-1"
          />
          <RefreshButton
            onRefresh={handleRefresh}
            lastUpdatedAt={lastUpdatedAt}
          />
        </>
      }
    >
      <Metadata title="Call Analytics" description="Call Analytics page" />
      {selectedTab === CallAnalyticsTab.VoiceAgent ? (
        <VoiceAgentHeroStrip key={refreshKey} filters={heroFilters} />
      ) : (
        <CallAnalyticsHeroStrip key={refreshKey} filters={heroFilters} />
      )}
      <FiltersSlotProvider value={filtersSlot}>
        {/*
          Pass `refreshKey` as a prop (instead of using `key={refreshKey}`)
          so containers can call refetch() on change without losing local
          state like the Call Volume bucket toggle selection.
        */}
        {selectedTab === CallAnalyticsTab.Overview && (
          <CallAnalyticsOverviewTab refreshKey={refreshKey} />
        )}
        {selectedTab === CallAnalyticsTab.VoiceAgent && (
          <VoiceAgentTab key={refreshKey} />
        )}
        {selectedTab === CallAnalyticsTab.CallQuality && isAdmin && (
          <CallQualityTab key={refreshKey} />
        )}
      </FiltersSlotProvider>
    </PageHeaderLayout>
  )
}

export default CallAnalyticsPage
